import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import {
  FilterPill,
  SrpBedTypeFilterValue,
  SrpCabinTypeFilterValue,
  SrpRearWheelFilterValue,
  SrpTruckOptionsFilterValue,
} from 'types/srpFilters';
import { FilterPillFn } from 'components/Srp/FilterPills/hooks/useCreateFilterPills';
import { useTranslation } from 'react-i18next';

type TruckOptionsFilterValue =
  | SrpCabinTypeFilterValue
  | SrpRearWheelFilterValue
  | SrpBedTypeFilterValue;
function selectedOptions(options: TruckOptionsFilterValue): [string, boolean][] {
  return Object.entries(options).filter(([, isSelected]) => isSelected);
}

export default function useTruckOptionsPillsCreators(): FilterPillFn {
  const { t } = useTranslation();

  function createCabinTypeOptionPill([optionKey]: [string, boolean]): FilterPill {
    return {
      name: optionKey,
      label: `${t(`srp.pills.${optionKey}`)} ${t('srp.pills.cabin')}`,
      resetState: { TruckOptions: { cabinType: { [optionKey]: false } } },
    };
  }

  function createRearWheelOptionPill([optionKey]: [string, boolean]): FilterPill {
    return {
      name: optionKey,
      label: t(`srp.pills.${optionKey}`),
      resetState: { TruckOptions: { rearWheel: { [optionKey]: false } } },
    };
  }

  function createBedTypeOptionPill([optionKey]: [string, boolean]): FilterPill {
    return {
      name: optionKey,
      label: `${t(`srp.pills.${optionKey}`)} ${t('srp.pills.bed')}`,
      resetState: { TruckOptions: { bedType: { [optionKey]: false } } },
    };
  }

  function createBedLengthOptionPill(bedLengthOptions: { min: number; max: number }): FilterPill {
    return {
      name: 'BedLength',
      label: t('srp.pills.bedRange', { min: bedLengthOptions.min, max: bedLengthOptions.max }),
      resetState: { TruckOptions: { bedLength: srpFilterInitialValues.TruckOptions.bedLength } },
    };
  }

  return (truckOptions: SrpTruckOptionsFilterValue): FilterPill[] => {
    const selectedCabinTypeOptions = selectedOptions(truckOptions.cabinType);
    const selectedRearWheelOptions = selectedOptions(truckOptions.rearWheel);
    const selectedBedTypeOptions = selectedOptions(truckOptions.bedType);
    const bedLengthOptions = truckOptions.bedLength;

    const cabinTypePills = selectedCabinTypeOptions.map(createCabinTypeOptionPill);
    const rearWheelPills = selectedRearWheelOptions.map(createRearWheelOptionPill);
    const bedTypePills = selectedBedTypeOptions.map(createBedTypeOptionPill);

    const isBedLengthNotDefault = bedLengthOptions.min !== 3 && bedLengthOptions.max !== 9;
    const bedLengthPills = isBedLengthNotDefault
      ? [createBedLengthOptionPill(bedLengthOptions)]
      : [];

    return [...cabinTypePills, ...rearWheelPills, ...bedTypePills, ...bedLengthPills];
  };
}
